// src/lib/admin-api.ts
import { turso as db } from '@/database/client';

let appSettingsTableReady = false;

async function ensureAppSettingsTable() {
  if (appSettingsTableReady) return;

  try {
    await db.execute(`
      CREATE TABLE IF NOT EXISTS app_settings (
        key TEXT PRIMARY KEY,
        value TEXT NOT NULL,
        updated_at TEXT DEFAULT CURRENT_TIMESTAMP
      )
    `);
    appSettingsTableReady = true;
  } catch (error) {
    console.error('Failed to ensure app_settings table:', error);
  }
}

export interface Song {
  id: string;
  title: string;
  artist: string;
  url: string;
  cover: string;
  created_at?: string;
  updated_at?: string;
}

export interface Advertisement {
  id: string;
  title: string;
  description: string;
  image: string;
  link: string;
  active: number;
  created_at?: string;
  updated_at?: string;
}

export interface UserInfo {
  id: string;
  email: string;
  name: string;
  created_at: string;
  last_active_at?: string;
  role?: 'super' | 'admin' | 'user';
}

// ============ APP SETTINGS ============

export async function getAppSetting(key: string): Promise<string | null> {
  try {
    await ensureAppSettingsTable();
    const result = await db.execute({
      sql: 'SELECT value FROM app_settings WHERE key = ? LIMIT 1',
      args: [key]
    });

    if (!result.rows || result.rows.length === 0) {
      return null;
    }

    const row = result.rows[0] as { value: string };
    return row.value;
  } catch (error) {
    console.error('Failed to fetch app setting:', error);
    return null;
  }
}

export async function setAppSetting(key: string, value: string): Promise<boolean> {
  try {
    await ensureAppSettingsTable();
    await db.execute({
      sql: `INSERT INTO app_settings (key, value)
            VALUES (?, ?)
            ON CONFLICT(key) DO UPDATE SET value = excluded.value, updated_at = CURRENT_TIMESTAMP`,
      args: [key, value]
    });
    return true;
  } catch (error) {
    console.error('Failed to save app setting:', error);
    return false;
  }
}

// ============ MUSIC PLAYLIST ============

export async function getAllSongs(): Promise<Song[]> {
  try {
    const result = await db.execute('SELECT * FROM music_playlist ORDER BY created_at DESC');
    return result.rows as Song[];
  } catch (error) {
    console.error('Failed to fetch songs:', error);
    return [];
  }
}

export async function createSong(song: Omit<Song, 'created_at' | 'updated_at'>): Promise<boolean> {
  try {
    await db.execute({
      sql: 'INSERT INTO music_playlist (id, title, artist, url, cover) VALUES (?, ?, ?, ?, ?)',
      args: [song.id, song.title, song.artist, song.url, song.cover]
    });
    return true;
  } catch (error) {
    console.error('Failed to create song:', error);
    return false;
  }
}

export async function updateSong(song: Song): Promise<boolean> {
  try {
    await db.execute({
      sql: 'UPDATE music_playlist SET title = ?, artist = ?, url = ?, cover = ?, updated_at = CURRENT_TIMESTAMP WHERE id = ?',
      args: [song.title, song.artist, song.url, song.cover, song.id]
    });
    return true;
  } catch (error) {
    console.error('Failed to update song:', error);
    return false;
  }
}

export async function deleteSong(id: string): Promise<boolean> {
  try {
    await db.execute({
      sql: 'DELETE FROM music_playlist WHERE id = ?',
      args: [id]
    });
    return true;
  } catch (error) {
    console.error('Failed to delete song:', error);
    return false;
  }
}

// ============ ADVERTISEMENTS ============

export async function getAllAds(): Promise<Advertisement[]> {
  try {
    const result = await db.execute('SELECT * FROM advertisements ORDER BY created_at DESC');
    return result.rows as Advertisement[];
  } catch (error) {
    console.error('Failed to fetch ads:', error);
    return [];
  }
}

export async function getActiveAds(): Promise<Advertisement[]> {
  try {
    const result = await db.execute('SELECT * FROM advertisements WHERE active = 1 ORDER BY created_at DESC');
    return result.rows as Advertisement[];
  } catch (error) {
    console.error('Failed to fetch active ads:', error);
    return [];
  }
}

export async function createAd(ad: Omit<Advertisement, 'created_at' | 'updated_at'>): Promise<boolean> {
  try {
    await db.execute({
      sql: 'INSERT INTO advertisements (id, title, description, image, link, active) VALUES (?, ?, ?, ?, ?, ?)',
      args: [ad.id, ad.title, ad.description, ad.image, ad.link, ad.active ? 1 : 0]
    });
    return true;
  } catch (error) {
    console.error('Failed to create ad:', error);
    return false;
  }
}

export async function updateAd(ad: Advertisement): Promise<boolean> {
  try {
    await db.execute({
      sql: 'UPDATE advertisements SET title = ?, description = ?, image = ?, link = ?, active = ?, updated_at = CURRENT_TIMESTAMP WHERE id = ?',
      args: [ad.title, ad.description, ad.image, ad.link, ad.active ? 1 : 0, ad.id]
    });
    return true;
  } catch (error) {
    console.error('Failed to update ad:', error);
    return false;
  }
}

export async function deleteAd(id: string): Promise<boolean> {
  try {
    await db.execute({
      sql: 'DELETE FROM advertisements WHERE id = ?',
      args: [id]
    });
    return true;
  } catch (error) {
    console.error('Failed to delete ad:', error);
    return false;
  }
}

export async function toggleAdActive(id: string): Promise<boolean> {
  try {
    await db.execute({
      sql: 'UPDATE advertisements SET active = NOT active, updated_at = CURRENT_TIMESTAMP WHERE id = ?',
      args: [id]
    });
    return true;
  } catch (error) {
    console.error('Failed to toggle ad:', error);
    return false;
  }
}

// ============ USERS ============

export interface UserDemographics {
  ageGroup?: 'under13' | 'teen' | 'adult';
  accountActive?: boolean;
  requiresParentalConsent?: boolean;
  parentalConsentStatus?: string;
  organizerMode?: 'student' | 'personal';
  isPaid?: boolean;
  isDiscount?: boolean;
  isBeta?: boolean;
}

export interface UserInfo {
  id: string;
  email: string;
  name: string;
  created_at: string;
  last_active_at?: string;
  demographics?: UserDemographics;
}

export async function getAllUsers(): Promise<UserInfo[]> {
  try {
    const result = await db.execute('SELECT id, email, name, role, created_at, last_active_at FROM users ORDER BY created_at DESC');
    const users = (result.rows as UserInfo[]).map((user) => ({
      ...user,
      role: user.role || 'user'
    }));
    
    // Get demographics for each user
    for (const user of users) {
      const demographics: UserDemographics = {};
      
      // Get age verification data
      const ageData = await db.execute({
        sql: "SELECT data_value FROM user_data WHERE user_id = ? AND data_type = 'age_verification' LIMIT 1",
        args: [user.id]
      });
      
      if (ageData.rows && ageData.rows.length > 0) {
        try {
          const restrictions = JSON.parse(ageData.rows[0].data_value as string);
          demographics.ageGroup = restrictions.ageGroup;
          demographics.accountActive = restrictions.accountActive;
          demographics.requiresParentalConsent = restrictions.requiresParentalConsent;
          demographics.parentalConsentStatus = restrictions.parentalConsentStatus;
        } catch (e) {
          console.error('Failed to parse age data for user:', user.id);
        }
      }
      
      // Get organizer preference
      const orgData = await db.execute({
        sql: "SELECT data_value FROM user_data WHERE user_id = ? AND data_type = 'preference' AND data_key = 'organizer_mode' LIMIT 1",
        args: [user.id]
      });
      
      if (orgData.rows && orgData.rows.length > 0) {
        demographics.organizerMode = orgData.rows[0].data_value as 'student' | 'personal';
      }
      
      // Get payment status (from user_data)
      const paymentData = await db.execute({
        sql: "SELECT data_key, data_value FROM user_data WHERE user_id = ? AND data_type = 'payment_status'",
        args: [user.id]
      });
      
      if (paymentData.rows && paymentData.rows.length > 0) {
        for (const row of paymentData.rows) {
          try {
            const value = JSON.parse(row.data_value as string);
            if (value.paid) demographics.isPaid = true;
            if (value.discount) demographics.isDiscount = true;
            if (value.beta) demographics.isBeta = true;
          } catch (e) {
            // ignore parse errors
          }
        }
      }
      
      user.demographics = demographics;
    }
    
    return users;
  } catch (error) {
    console.error('Failed to fetch users:', error);
    return [];
  }
}

export async function getUserCount(): Promise<number> {
  try {
    const result = await db.execute('SELECT COUNT(*) as count FROM users');
    return (result.rows[0] as any).count || 0;
  } catch (error) {
    console.error('Failed to get user count:', error);
    return 0;
  }
}

export async function deleteUser(userId: string): Promise<boolean> {
  try {
    console.log('[Admin] Starting deletion of user:', userId);
    
    const errors: string[] = [];
    
    // Delete from all related tables (ignore errors for missing data)
    
    // 1. Delete user_data
    try {
      const result1 = await db.execute({
        sql: 'DELETE FROM user_data WHERE user_id = ?',
        args: [userId]
      });
      console.log('[Admin] Deleted user_data:', result1.rowsAffected, 'rows');
    } catch (e: any) {
      console.warn('[Admin] Error deleting user_data:', e.message);
      errors.push('user_data: ' + e.message);
    }
    
    // 2. Delete messages (sent and received)
    try {
      const result2 = await db.execute({
        sql: 'DELETE FROM messages WHERE sender_id = ? OR receiver_id = ?',
        args: [userId, userId]
      });
      console.log('[Admin] Deleted messages:', result2.rowsAffected, 'rows');
    } catch (e: any) {
      console.warn('[Admin] Error deleting messages:', e.message);
      errors.push('messages: ' + e.message);
    }
    
    // 3. Delete sessions
    try {
      const result3 = await db.execute({
        sql: 'DELETE FROM sessions WHERE user_id = ?',
        args: [userId]
      });
      console.log('[Admin] Deleted sessions:', result3.rowsAffected, 'rows');
    } catch (e: any) {
      console.warn('[Admin] Error deleting sessions:', e.message);
      errors.push('sessions: ' + e.message);
    }
    
    // 4. Try to disable foreign key constraints temporarily and delete user
    try {
      // Disable foreign keys
      await db.execute('PRAGMA foreign_keys = OFF');
      
      const result4 = await db.execute({
        sql: 'DELETE FROM users WHERE id = ?',
        args: [userId]
      });
      
      // Re-enable foreign keys
      await db.execute('PRAGMA foreign_keys = ON');
      
      console.log('[Admin] Deleted user. Rows affected:', result4.rowsAffected);
      
      if (result4.rowsAffected === 0) {
        throw new Error('User not found or already deleted');
      }
      
      if (errors.length > 0) {
        console.warn('[Admin] User deleted but had errors in related data:', errors);
      }
      
      return true;
    } catch (e: any) {
      // Re-enable foreign keys even if delete failed
      try {
        await db.execute('PRAGMA foreign_keys = ON');
      } catch {}
      
      console.error('[Admin] Failed to delete user from users table:', e.message);
      errors.push('users: ' + e.message);
      throw new Error(`Failed to delete user: ${errors.join(', ')}`);
    }
  } catch (error: any) {
    console.error('[Admin] Failed to delete user:', error);
    throw error;
  }
}

// ============ USER DATA (Calendar, Settings, etc.) ============

export async function saveUserData(
  userId: string,
  dataType: string,
  dataKey: string,
  dataValue: any
): Promise<boolean> {
  try {
    const id = `${userId}_${dataType}_${dataKey}_${Date.now()}`;
    const valueStr = typeof dataValue === 'string' ? dataValue : JSON.stringify(dataValue);
    
    await db.execute({
      sql: `INSERT INTO user_data (id, user_id, data_type, data_key, data_value) 
            VALUES (?, ?, ?, ?, ?)
            ON CONFLICT(id) DO UPDATE SET data_value = ?, updated_at = CURRENT_TIMESTAMP`,
      args: [id, userId, dataType, dataKey, valueStr, valueStr]
    });
    
    return true;
  } catch (error) {
    console.error('Failed to save user data:', error);
    return false;
  }
}

export async function getUserData(
  userId: string,
  dataType: string,
  dataKey?: string
): Promise<any[]> {
  try {
    let sql = 'SELECT * FROM user_data WHERE user_id = ? AND data_type = ?';
    const args: any[] = [userId, dataType];
    
    if (dataKey) {
      sql += ' AND data_key = ?';
      args.push(dataKey);
    }
    
    sql += ' ORDER BY created_at DESC';
    
    const result = await db.execute({ sql, args });
    return result.rows.map(row => {
      try {
        return {
          ...row,
          data_value: JSON.parse((row as any).data_value)
        };
      } catch {
        return row;
      }
    });
  } catch (error) {
    console.error('Failed to get user data:', error);
    return [];
  }
}
